import { useEffect, useMemo, useState } from 'react'
import { Gantt, Task as GanttTask, ViewMode } from 'gantt-task-react'
import 'gantt-task-react/dist/index.css'
import { GanttChartSquare, Link2, Trash2 } from 'lucide-react'
import {
  useProjectStore,
  type Task,
  type TaskDependency,
} from '../../store/projectStore'
import { usePermissions } from '../../hooks/usePermissions'
import TaskDetailModal from './TaskDetailModal'

interface GanttChartProps {
  projectId: number
}

function toDate(v?: string | null) {
  if (!v) return null
  const d = new Date(v + 'T00:00:00')
  if (isNaN(d.getTime())) return null
  return d
}

function fallbackEnd(start: Date) {
  const d = new Date(start)
  d.setDate(d.getDate() + 1)
  return d
}

export default function GanttChart({ projectId }: GanttChartProps) {
  const { gantt, fetchGantt, updateTask, addDependency, removeDependency } = useProjectStore()
  const { can } = usePermissions()
  const [view, setView] = useState<ViewMode>(ViewMode.Week)
  const [openTask, setOpenTask] = useState<Task | null>(null)
  const [linkSource, setLinkSource] = useState<Task | null>(null)
  const [linkTarget, setLinkTarget] = useState<number | ''>('')
  const [linkError, setLinkError] = useState('')

  const canEdit = can('admin_project_monitoring', 'edit') || can('pwa_project_monitoring', 'edit')
  const canDelete = can('admin_project_monitoring', 'delete') || can('pwa_project_monitoring', 'delete')

  useEffect(() => {
    fetchGantt(projectId)
  }, [projectId, fetchGantt])

  const ganttTasks: GanttTask[] = useMemo(() => {
    if (!gantt) return []
    return gantt.tasks
      .filter((t) => t.start_date || t.due_date)
      .map((t) => {
        const start = toDate(t.start_date) || toDate(t.due_date) || new Date()
        const end = toDate(t.due_date) || fallbackEnd(start)
        const safeEnd = end.getTime() <= start.getTime() ? fallbackEnd(start) : end
        const deps = gantt.dependencies
          .filter((d) => d.task_id === t.id)
          .map((d) => String(d.depends_on_task_id))
        return {
          id: String(t.id),
          name: t.title,
          start,
          end: safeEnd,
          progress: t.completion_percent || 0,
          type: 'task' as const,
          isDisabled: !canEdit,
          dependencies: deps,
          styles: {
            backgroundColor: t.status === 'done' ? '#10b981' : t.status === 'blocked' ? '#ef4444' : '#3b82f6',
            progressColor: '#1d4ed8',
          },
        }
      })
  }, [gantt, canEdit])

  const handleDateChange = async (task: GanttTask) => {
    const original = gantt?.tasks.find((t) => String(t.id) === task.id)
    if (!original) return
    const startStr = task.start.toISOString().slice(0, 10)
    const endStr = task.end.toISOString().slice(0, 10)
    try {
      await updateTask(original.id, {
        start_date: startStr,
        due_date: endStr,
      })
      await fetchGantt(projectId)
    } catch {
      await fetchGantt(projectId)
    }
  }

  const handleProgressChange = async (task: GanttTask) => {
    const original = gantt?.tasks.find((t) => String(t.id) === task.id)
    if (!original) return
    try {
      await updateTask(original.id, { completion_percent: Math.round(task.progress) })
      await fetchGantt(projectId)
    } catch {
      await fetchGantt(projectId)
    }
  }

  const handleAddDependency = async () => {
    if (!linkSource || linkTarget === '') return
    setLinkError('')
    try {
      await addDependency(linkSource.id, Number(linkTarget))
      setLinkSource(null)
      setLinkTarget('')
      await fetchGantt(projectId)
    } catch (e) {
      setLinkError(e instanceof Error ? e.message : 'Failed to add dependency')
    }
  }

  const handleRemoveDependency = async (dep: TaskDependency) => {
    if (!confirm('Remove this dependency?')) return
    await removeDependency(dep.task_id, dep.id)
    await fetchGantt(projectId)
  }

  if (!gantt) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
        <GanttChartSquare className="mx-auto h-10 w-10 text-gray-400 animate-pulse" />
        <p className="mt-3 text-sm text-gray-500">Loading timeline…</p>
      </div>
    )
  }

  if (ganttTasks.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
        <GanttChartSquare className="mx-auto h-10 w-10 text-gray-400" />
        <h3 className="mt-3 text-sm font-medium text-gray-900">No tasks with dates yet</h3>
        <p className="mt-1 text-sm text-gray-500">Add start and due dates to tasks to see them on the timeline.</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white text-sm">
          {[
            { v: ViewMode.Day, label: 'Day' },
            { v: ViewMode.Week, label: 'Week' },
            { v: ViewMode.Month, label: 'Month' },
          ].map((opt) => (
            <button
              key={opt.label}
              onClick={() => setView(opt.v)}
              className={`px-3 py-1.5 ${view === opt.v ? 'bg-primary-600 text-white' : 'hover:bg-gray-50'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {canEdit && (
          <button
            onClick={() => setLinkSource(gantt.tasks[0] || null)}
            disabled={gantt.tasks.length < 2}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" />
            Add dependency
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <Gantt
          tasks={ganttTasks}
          viewMode={view}
          onDateChange={handleDateChange}
          onProgressChange={handleProgressChange}
          onDoubleClick={(task) => {
            const t = gantt.tasks.find((x) => String(x.id) === task.id)
            if (t) setOpenTask(t)
          }}
          listCellWidth=""
          columnWidth={view === ViewMode.Month ? 200 : view === ViewMode.Week ? 100 : 50}
        />
      </div>

      {gantt.dependencies.length > 0 && canEdit && (
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Dependencies</h3>
          <ul className="space-y-1">
            {gantt.dependencies.map((d) => {
              const from = gantt.tasks.find((t) => t.id === d.depends_on_task_id)
              const to = gantt.tasks.find((t) => t.id === d.task_id)
              return (
                <li key={d.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">
                    <span className="font-medium">{from?.title || `#${d.depends_on_task_id}`}</span>
                    {' → '}
                    <span className="font-medium">{to?.title || `#${d.task_id}`}</span>
                  </span>
                  <button
                    onClick={() => handleRemoveDependency(d)}
                    className="text-red-600 hover:text-red-700"
                    aria-label="Remove dependency"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {linkSource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <h3 className="mb-3 text-sm font-medium">Add dependency</h3>
            {linkError && <div className="mb-2 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{linkError}</div>}
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Task</label>
                <select
                  value={linkSource.id}
                  onChange={(e) => {
                    const id = Number(e.target.value)
                    const t = gantt.tasks.find((x) => x.id === id)
                    if (t) setLinkSource(t)
                  }}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                >
                  {gantt.tasks.map((t) => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 text-center">depends on</p>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Predecessor</label>
                <select
                  value={linkTarget}
                  onChange={(e) => setLinkTarget(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                >
                  <option value="">Select task…</option>
                  {gantt.tasks.filter((t) => t.id !== linkSource.id).map((t) => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setLinkSource(null); setLinkTarget(''); setLinkError('') }}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleAddDependency} disabled={linkTarget === ''}
                      className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <TaskDetailModal
        isOpen={openTask !== null}
        task={openTask}
        projectId={projectId}
        canEdit={canEdit}
        canDelete={canDelete}
        onClose={() => { setOpenTask(null); fetchGantt(projectId) }}
      />
    </div>
  )
}
